/**
 * When an LSA call happened and how long the caller held (Shawn, 2026-10-28). The time
 * is the account's own clock, as Google reports it, with that zone's short name.
 * No imports and only erasable types, so node --test loads it.
 */
type Call = { caller_name?: unknown; caller_phone?: unknown; caller_phone_last4?: unknown; lsa_lead_id?: unknown; google_lead_id?: unknown; call_date?: unknown; call_time?: unknown; call_time_zone?: unknown; call_occurred_at?: unknown; hold_seconds?: unknown };

export function callTimeLabel(c: Call): string | null {
  const match = /^(\d{2}):(\d{2})/.exec(String(c.call_time ?? ""));
  if (!match) return null;
  const hour = Number(match[1]);
  const time = `${hour % 12 || 12}:${match[2]} ${hour < 12 ? "AM" : "PM"}`;
  // The zone's short name (PDT or PST) only needs the day: the exact instant when Google
  // gave one, else midday on the call's date.
  const day = /^\d{4}-\d{2}-\d{2}/.exec(String(c.call_date ?? ""))?.[0];
  const at = new Date(c.call_occurred_at ? String(c.call_occurred_at) : day ? `${day}T12:00:00Z` : "");
  if (!c.call_time_zone || Number.isNaN(at.getTime())) return time;
  try {
    const zone = new Intl.DateTimeFormat("en-US", { timeZone: String(c.call_time_zone), timeZoneName: "short" })
      .formatToParts(at).find((p) => p.type === "timeZoneName")?.value;
    return zone ? `${time} ${zone}` : time;
  } catch { return time; }
}

export function holdLabel(c: Call): string | null {
  if (c.hold_seconds == null || c.hold_seconds === "") return null;
  const sec = Math.round(Number(c.hold_seconds));
  if (!Number.isFinite(sec) || sec < 0) return null;
  if (sec === 0) return "No hold";
  return sec < 60 ? `${sec} sec` : `${Math.floor(sec / 60)} min${sec % 60 ? ` ${sec % 60} sec` : ""}`;
}

export type Territory = { id: string; name: string; calls: number };

/** "All territories" first, then each one with its calls. Only worth showing with two or more. */
export function territoryOptions(list: Territory[] | null | undefined) {
  const items = list || [];
  return [{ value: "", name: "All territories", count: items.reduce((n, t) => n + t.calls, 0) },
    ...items.map((t) => ({ value: t.id, name: t.name, count: t.calls }))];
}

/** The Lead ID as Google's LSA dashboard shows it (google_lead_id). lsa_lead_id is the call's
 * conversation id, a different number, so it is never shown as the lead (Shawn, 2026-11-03).
 * Null until the nightly sync fills it on an older call. */
export function leadIdOf(call: { google_lead_id?: unknown } | null | undefined): string | null {
  const value = call?.google_lead_id;
  return value == null || value === "" ? null : String(value).split("/").pop() || null;
}

/** A US number as people write it, "(916) 555-0123 ext 12"; anything else as Google gave it. */
export function phoneLabel(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const ext = /\s*ext\.?\s*(\d+)\s*$/i.exec(raw);
  const digits = (ext ? raw.slice(0, ext.index) : raw).replace(/\D/g, "");
  const us = /^1?(\d{3})(\d{3})(\d{4})$/.exec(digits);
  return us ? `(${us[1]}) ${us[2]}-${us[3]}${ext ? ` ext ${ext[1]}` : ""}` : raw;
}

/** Google rarely gives LSA callers' names, so the caller's number identifies the call (Shawn, 2026-10-28;
 * the whole number, Neil, 2026-10-30). Calls saved before the number was kept read by its last four digits. */
export function callerLabel(c: Call): string {
  const name = String(c.caller_name ?? "").trim();
  if (name) return name;
  const phone = phoneLabel(c.caller_phone);
  if (phone) return phone;
  return /^\d{4}$/.test(String(c.caller_phone_last4 ?? "")) ? `Caller ending ${c.caller_phone_last4}` : "Unknown caller";
}
